import React, { memo, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { validators } from 'investira.sdk';
import {
  Stack,
  Icon,
  Grid,
  Card,
  TextField,
  LoadingButton,
  Switch,
  FormControlLabel,
  Typography
} from 'investira.react.components';
import { VALIDATIONS } from '../../../const';
import { DatePicker } from '../../../components/molecules';
import services from '../../../services';
import withResponseHandling from '../../../hoc/withResponseHandling';

const EnquadramentoEdit = memo(props => {
  const location = useLocation();
  const navigate = useNavigate();

  const locationState = location.state;

  const [hasVigencia, setHasVigencia] = useState(false);

  const VALIDATION_SCHEMA = Yup.object().shape({
    enquadramento: Yup.string().max(128, 'Máximo de 128 caracteres').required(VALIDATIONS.REQUIRED),
    vigencia_inicio: Yup.date()
      .test(
        'data-inicio',
        '"Vigência início" não pode ser maior do que a data de fim',
        function (value) {
          const { vigencia_fim } = this.parent;
          if (!value || !vigencia_fim) return true;
          return (
            dayjs(value).isBefore(dayjs(vigencia_fim)) || dayjs(value).isSame(dayjs(vigencia_fim))
          );
        }
      )
      .nullable(),
    vigencia_fim: Yup.date()
      .test('data-fim', '"Vigência fim" não pode ser maior do que a data de fim', function (value) {
        const { vigencia_inicio } = this.parent;
        if (!value || !vigencia_inicio) return true;
        return (
          dayjs(value).isAfter(dayjs(vigencia_inicio)) ||
          dayjs(value).isSame(dayjs(vigencia_inicio))
        );
      })
      .nullable()
  });

  const formik = useFormik({
    initialValues: {
      enquadramento: locationState.enquadramento,
      vigencia_inicio: locationState.vigencia_inicio,
      vigencia_fim: locationState.vigencia_fim
    },
    validationSchema: VALIDATION_SCHEMA,
    validateOnMount: false,
    onSubmit: (pValues, pActions) => {
      pActions.setSubmitting(true);
      services.enquadramentos.modify(
        {
          data: {
            enquadramento_id: locationState.enquadramento_id,
            enquadramento: pValues.enquadramento,
            vigencia_inicio: pValues.vigencia_inicio,
            vigencia_fim: pValues.vigencia_fim
          }
        },
        rRes => {
          if (
            rRes?.data &&
            validators.isTrue(locationState?.assinatura) &&
            validators.isEmpty(locationState?.assinatura_estagio)
          ) {
            services.outputs.registrar(
              {
                enquadramento_id: locationState.enquadramento_id,
                output_tipo: 'CFENQ'
              },
              _rRes => {},
              _rErr => {}
            );
          }
          navigate(`${location.pathname.replace(/\/edit$/, '')}`);
        },
        rErr => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    }
  });

  useEffect(() => {
    if (hasVigencia) {
      formik.setFieldValue('vigencia_inicio', dayjs().startOf('year'));
      formik.setFieldValue('vigencia_fim', dayjs().endOf('year'));
    } else {
      formik.setFieldValue('vigencia_inicio', null);
      formik.setFieldValue('vigencia_fim', null);
    }
  }, [hasVigencia]);

  return (
    <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack spacing={2}>
            <Card>
              <Stack sx={{ py: 2, px: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasVigencia}
                      onChange={() => {
                        setHasVigencia(!hasVigencia);
                      }}
                      name="insertType"
                      size="small"
                    />
                  }
                  label={
                    <Typography color="textSecondary" variant="caption">
                      Adicionar Vigência
                    </Typography>
                  }
                />
              </Stack>
              <Stack spacing={2} sx={{ pt: 3, pb: 2, px: 2 }}>
                {hasVigencia && (
                  <Stack spacing={2}>
                    {!validators.isNull(formik.values.vigencia_inicio) && (
                      <DatePicker
                        label="Vigência Início"
                        value={formik.values.vigencia_inicio}
                        onChange={newValue => {
                          formik.setFieldValue('vigencia_inicio', newValue);
                        }}
                        slotProps={{
                          textField: {
                            variant: 'outlined',
                            error: formik.errors.vigencia_inicio,
                            helperText: formik.errors.vigencia_inicio
                          }
                        }}
                        renderInput={params => <TextField {...params} />}
                      />
                    )}
                    {!validators.isNull(formik.values.vigencia_fim) && (
                      <DatePicker
                        label="Vigência Fim"
                        value={formik.values.vigencia_fim}
                        onChange={newValue => {
                          formik.setFieldValue('vigencia_fim', newValue);
                        }}
                        slotProps={{
                          textField: {
                            variant: 'outlined',
                            error: formik.errors.vigencia_fim,
                            helperText: formik.errors.vigencia_fim
                          }
                        }}
                        renderInput={params => <TextField {...params} />}
                      />
                    )}
                  </Stack>
                )}
                <TextField
                  id="enquadramento"
                  label="Enquadramento"
                  value={formik.values.enquadramento}
                  onChange={formik.handleChange}
                  variant="outlined"
                  placeholder={'Digite um nome para o enquadramento'}
                  fullWidth
                  disabled={props.isSubmitting}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                <Stack direction="row" justifyContent="flex-end">
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    startIcon={<Icon iconName="save" size={16} />}
                    sx={{ px: 2 }}
                    type="submit"
                    disabled={formik.isSubmitting || !formik.isValid}
                    loading={formik.isSubmitting}>
                    Salvar
                  </LoadingButton>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
});

export default withResponseHandling(EnquadramentoEdit);
